import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence } from 'framer-motion';
import { useTutorial } from '../../contexts/TutorialContext';
import { STEP_TYPES } from '../../constants/tutorialConstants';
import TutorialModal from './TutorialModal';
import TutorialSpotlight from './TutorialSpotlight';
import TutorialProgress from './TutorialProgress';

export default function TutorialOverlay() {
  const {
    state,
    currentStepData,
    progress,
    startTutorial,
    nextStep,
    previousStep,
    skipStep,
    completeTutorial,
    dismissTutorial,
    resetTutorial,
    shouldShowTutorial,
  } = useTutorial();

  const announceRef = useRef(null);
  const prevStepRef = useRef(null);

  // Auto-start tutorial for new users
  useEffect(() => {
    if (!state.isActive && !state.isLoading && shouldShowTutorial()) {
      startTutorial();
    }
  }, [state.isActive, state.isLoading, shouldShowTutorial, startTutorial]);

  // Announce step changes to screen readers
  useEffect(() => {
    if (currentStepData && announceRef.current && currentStepData.id !== prevStepRef.current) {
      prevStepRef.current = currentStepData.id;
      announceRef.current.textContent =
        `Tutorial step ${progress.current} of ${progress.total}: ${currentStepData.title}`;
    }
  }, [currentStepData, progress]);

  // Keyboard navigation
  useEffect(() => {
    if (!state.isActive) return;

    const handleKeyDown = (e) => {
      // Don't capture keys when user is typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }

      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          dismissTutorial();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault();
          nextStep();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault();
          previousStep();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.isActive, nextStep, previousStep, dismissTutorial]);

  const handleRestart = () => {
    const track = state.activeTrack;
    resetTutorial(track).then(() => startTutorial(track));
  };

  if (!state.isActive || !currentStepData) return (
    // Keep the live region in DOM even when inactive
    <div ref={announceRef} role="status" aria-live="polite" className="sr-only" />
  );

  const showPrevious = state.currentStepIndex > 0;

  const content = (
    <>
      {/* Screen reader announcements */}
      <div ref={announceRef} role="status" aria-live="polite" className="sr-only" />

      <AnimatePresence mode="wait">
        {currentStepData.type === STEP_TYPES.MODAL && (
          <TutorialModal
            key={currentStepData.id}
            step={currentStepData}
            onNext={nextStep}
            onDismiss={dismissTutorial}
            onComplete={completeTutorial}
            onRestart={handleRestart}
          />
        )}

        {currentStepData.type === STEP_TYPES.SPOTLIGHT && (
          <TutorialSpotlight
            key={currentStepData.id}
            step={currentStepData}
            onNext={nextStep}
            onPrevious={previousStep}
            onSkip={skipStep}
            showPrevious={showPrevious}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {state.isActive && <TutorialProgress progress={progress} />}
      </AnimatePresence>
    </>
  );

  return createPortal(content, document.body);
}
